'use client';

import { useEffect } from 'react';

import { withSentry } from '~/lib/sentry-client';

interface Props {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function GlobalError({ error, reset }: Props) {
  useEffect(() => {
    withSentry((Sentry) => Sentry.captureException(error));
  }, [error]);

  return (
    <html lang="en">
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Error - Unur Cashmere</title>
        <style>{`
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            background-color: #faf8f5;
            color: #3e3b37;
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 1rem;
          }

          .global-error-container {
            max-width: 42rem;
            text-align: center;
          }

          h1 {
            font-size: 2.5rem;
            font-weight: 700;
            margin-bottom: 1rem;
            line-height: 1.2;
          }

          p {
            font-size: 1.125rem;
            color: #6b6b6b;
            margin-bottom: 2rem;
            line-height: 1.5;
          }

          .button-group {
            display: flex;
            gap: 1rem;
            justify-content: center;
            flex-wrap: wrap;
          }

          button, a {
            display: inline-block;
            padding: 0.75rem 1.5rem;
            font-size: 1rem;
            font-weight: 500;
            text-decoration: none;
            border-radius: 0.5rem;
            border: none;
            cursor: pointer;
            transition: all 0.2s;
          }

          .btn-primary {
            background-color: #3e3b37;
            color: #ffffff;
          }

          .btn-primary:hover {
            background-color: #2d2b27;
          }

          .btn-secondary {
            background-color: #ffffff;
            color: #3e3b37;
            border: 1px solid #d1d1d1;
          }

          .btn-secondary:hover {
            background-color: #f5f5f5;
          }

          @media (max-width: 640px) {
            h1 {
              font-size: 2rem;
            }

            p {
              font-size: 1rem;
            }

            .button-group {
              flex-direction: column;
            }

            button, a {
              width: 100%;
            }
          }
        `}</style>
      </head>
      <body>
        <div className="global-error-container">
          <h1>Something went wrong</h1>
          <p>We encountered an unexpected error. Please try again, or return to the home page.</p>
          <div className="button-group">
            <button onClick={() => reset()} className="btn-primary">
              Try Again
            </button>
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
            <a href="/" className="btn-secondary">
              Back to Home
            </a>
          </div>
        </div>
      </body>
    </html>
  );
}
